import React, { useState } from 'react';
import { Shirt, Users, Award, Clock, Check, ArrowRight, Globe, Star, Crown, Calendar } from 'lucide-react';
import { siteData } from '../data/mock';

const ClothierPage = () => {
  const { clothier } = siteData;
  const [selectedCategory, setSelectedCategory] = useState('all');

  const filteredGallery = selectedCategory === 'all' 
    ? clothier.gallery 
    : clothier.gallery.filter(item => item.category === selectedCategory);

  return (
    <div className="min-h-screen pt-24">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-background">
          <img 
            src="https://customer-assets.emergentagent.com/job_18d2d639-f4e1-4915-80f9-3d48232ed8cc/artifacts/rn2olz2c_Romero%20in%20Suit%20Shop%20%281%29.png" 
            alt="Romero Sumpter in his tailor shop" 
            className="hero-image"
          />
          <div className="hero-overlay"></div>
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="hero-content">
            <h1 className="brand-display mb-6">
              {clothier.title}
            </h1>
            <p className="body-large mb-4 text-lime-400">
              {clothier.subtitle}
            </p>
            <p className="body-medium mb-12 max-w-lg">
              {clothier.description}
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a 
                href="mailto:romero.sumpter@gmail.com?subject=Clothier Consultation Request"
                className="btn-primary"
              >
                Email for Consultation <ArrowRight size={18} className="ml-2" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="heading-2 mb-6">Custom Tailoring Services</h2>
            <p className="body-large text-gray-300 max-w-2xl mx-auto">
              Each piece is meticulously crafted to your exact specifications, ensuring the perfect fit and style for your unique needs.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-4 gap-8">
            {clothier.services.map((service) => {
              // Define icons for each service
              const getServiceIcon = (id) => {
                switch(id) {
                  case 1: return <Globe size={32} className="text-yellow-400" />;
                  case 2: return <Shirt size={32} className="text-yellow-400" />;
                  case 3: return <Crown size={32} className="text-yellow-400" />;
                  case 4: return <Star size={32} className="text-yellow-400" />;
                  default: return <Shirt size={32} className="text-yellow-400" />;
                }
              };

              return (
                <div key={service.id} className="service-card">
                  <div className="flex items-center justify-between mb-4">
                    {getServiceIcon(service.id)}
                    <span className={`caption ${service.status === 'coming-soon' ? 'text-gray-400' : 'text-yellow-400'}`}>
                      {service.startingPrice}
                    </span>
                  </div>
                  <h3 className="heading-3 mb-4">{service.name}</h3>
                  <p className="body-small text-gray-300 mb-6">
                    {service.description}
                  </p>
                  <ul className="space-y-2 mb-8">
                    {service.features.map((feature, index) => (
                      <li key={index} className="flex items-center body-small text-gray-300">
                        <Check size={16} className="text-yellow-400 mr-2 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                  {service.status === 'coming-soon' ? (
                    <button className="btn-secondary w-full opacity-50 cursor-not-allowed">
                      <Calendar size={16} className="mr-2" />
                      Coming Soon
                    </button>
                  ) : service.external ? (
                    <a 
                      href={service.link} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="btn-primary w-full text-center"
                    >
                      <Globe size={16} className="mr-2" />
                      Order Online
                    </a>
                  ) : (
                    <a 
                      href="mailto:romero.sumpter@gmail.com?subject=Clothier Consultation Request"
                      className="btn-secondary w-full text-center"
                    >
                      <ArrowRight size={16} className="mr-2" />
                      Email for Consultation
                    </a>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-gray-900">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="heading-2 mb-6">Our Tailoring Process</h2>
            <p className="body-large text-gray-300 max-w-2xl mx-auto">
              From initial consultation to final delivery, every step is designed to ensure your complete satisfaction.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {clothier.process.map((step) => (
              <div key={step.step} className="text-center">
                <div className="w-16 h-16 bg-lime-400 rounded-full flex items-center justify-center mx-auto mb-4 text-black font-bold text-xl">
                  {step.step}
                </div>
                <h3 className="heading-3 mb-3">{step.title}</h3>
                <p className="body-small text-gray-300">
                  {step.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-gray-900">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <Award size={48} className="text-lime-400 mx-auto mb-4" />
              <h3 className="heading-3 mb-3">Master Craftsmanship</h3>
              <p className="body-small text-gray-300">
                Over 20 years of experience in custom tailoring with attention to every detail.
              </p>
            </div>
            <div className="text-center">
              <Users size={48} className="text-lime-400 mx-auto mb-4" />
              <h3 className="heading-3 mb-3">Personal Service</h3>
              <p className="body-small text-gray-300">
                One-on-one consultations and fittings to ensure your vision becomes reality.
              </p>
            </div>
            <div className="text-center">
              <Clock size={48} className="text-lime-400 mx-auto mb-4" />
              <h3 className="heading-3 mb-3">Timely Delivery</h3>
              <p className="body-small text-gray-300">
                Professional timelines with quality that never compromises on your schedule.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-20">
        <div className="container mx-auto px-4 text-center">
          <h2 className="heading-2 mb-6">Ready for Your Custom Piece?</h2>
          <p className="body-large text-gray-300 mb-12 max-w-2xl mx-auto">
            Schedule a consultation today and let's create something exceptional that reflects your unique style and personality.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="mailto:romero.sumpter@gmail.com?subject=Clothier Consultation Request"
              className="btn-primary"
            >
              Email for Consultation <ArrowRight size={18} className="ml-2" />
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ClothierPage;